// "What needs you" for PCs that start from the boot server (spec/13 §3.2, F-BOOT-30): each problem is a card — one plain
// sentence, the PCs it is about as key caps, how long, and the one button that fixes it (never a code or a raw error). The
// same cards are drawn on the Floor of a venue with both products and on the Floor of PCs of a venue with Clavian Boot only.
//
// A card is read from the per-PC boot summary and the boot server's status, so a member needs `boot:view` to see any of
// them. The button is shown only to a member who holds the permission for its fix: choosing an image for a class needs
// `stations:manage` with `boot:manage`, changing a game disk's letter and restarting PCs when free need `boot:manage`.
// Opening the boot server, opening a PC and the motherboard explainer need nothing more than seeing the card.

import type { BootFloorPc, StationClass } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, cx, Dialog, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { Access } from '../api/queries';
import { ConfirmDialog } from '../boot/ConfirmDialog';
import { durationWords } from '../boot/floor-boot';
import { formatBytes } from '../boot/format';
import { useBootServers, useRestartIdleBootPcs, type BootScope } from '../boot/queries';
import { Icon } from '../shell/nav-icons';
import { type BootProblemCard, bootProblemCards, coveredByCards } from './boot-problems';
import type { FloorStationView } from './live';

import './boot-problems.css';

const BOOT_VIEW = 'boot:view';
const BOOT_MANAGE = 'boot:manage';
const STATIONS_MANAGE = 'stations:manage';
/** How many station numbers a card lists before "+ n more". */
const KEYCAPS_SHOWN = 10;

type ConsoleT = ReturnType<typeof useTranslation>['t'];

/**
 * The cards for this venue's floor. Empty for a member who cannot read boot, a venue without the add-on, or while the
 * boot summary has not been read. Time-dependent words ("for 6 min") re-read every 30 seconds.
 */
export function useBootProblems(input: {
  readonly scope: BootScope;
  readonly access: Access;
  readonly views: readonly FloorStationView[];
  readonly bootFull: ReadonlyMap<string, BootFloorPc>;
  readonly classById: ReadonlyMap<string, StationClass>;
}): readonly BootProblemCard[] {
  const { scope, access, views, bootFull, classById } = input;
  const canRead = access.status === 'ready' && access.can(BOOT_VIEW);
  const servers = useBootServers(scope, canRead);
  return useMemo(() => {
    if (!canRead || servers.data === undefined) return [];
    return bootProblemCards({
      stations: views.filter((item) => item.state !== 'disabled').map((item) => ({ stationId: item.station.stationId, number: item.station.number, classId: item.station.classId })),
      pcs: [...bootFull.values()],
      servers: servers.data,
      classNames: new Map([...classById].map(([id, klass]) => [id, klass.name] as const)),
    });
  }, [canRead, servers.data, views, bootFull, classById]);
}

export { coveredByCards };

/** The section in the panel: its cards, one list. Nothing when there are none. */
export function BootProblemsSection({
  cards,
  access,
  venueSlug,
  scope,
  bootFull,
  onOpen,
}: {
  readonly cards: readonly BootProblemCard[];
  readonly access: Access;
  readonly venueSlug: string;
  readonly scope: BootScope;
  readonly bootFull: ReadonlyMap<string, BootFloorPc>;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const now = useNowEvery(30_000);
  if (cards.length === 0) return null;
  return (
    <section className="console-floor__panel-section" aria-label={t('bootGuide.problems.title')}>
      <h3 className="console-floor__panel-subtitle">
        <span>{t('bootGuide.problems.title')}</span>
        <Badge tone={cards.some((card) => card.tone === 'danger') ? 'danger' : 'warn'}>{cards.length}</Badge>
      </h3>
      <ul className="console-problems">
        {cards.map((card) => (
          <li key={card.id} className={cx('console-problem', `console-problem--${card.tone}`)} data-problem={card.kind}>
            <ProblemCard card={card} access={access} venueSlug={venueSlug} scope={scope} bootFull={bootFull} now={now} onOpen={onOpen} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function useNowEvery(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** The card's one sentence, in the member's words. */
export function problemSentence(card: BootProblemCard, t: ConsoleT, now: number): string {
  const count = card.numbers.length;
  const p = card.params;
  switch (card.kind) {
    case 'noImage':
      return typeof p['class'] === 'string' ? t('bootGuide.problems.noImage', { count, class: p['class'] }) : t('bootGuide.problems.noImageNoClass', { count });
    case 'licence':
      return t('bootGuide.problems.licence', { count });
    case 'noServer':
      return t('bootGuide.problems.noServer', { count });
    case 'serverOffline': {
      if (card.sinceIso === null) return t('bootGuide.problems.serverOfflineNever');
      return t('bootGuide.problems.serverOffline', { minutes: Math.max(1, Math.floor((now - Date.parse(card.sinceIso)) / 60_000)) });
    }
    case 'otherBoard':
      return t('bootGuide.problems.otherBoard', { count });
    case 'startFailed':
      return t('bootGuide.problems.startFailed', { count });
    case 'notReaching':
      return t('bootGuide.problems.notReaching');
    case 'letter':
      return t('bootGuide.problems.letter', { count, letter: String(p['letter']) });
    case 'behind':
      return t('bootGuide.problems.behind', { count, image: String(p['image']), version: Number(p['version']) });
    case 'diskLow':
      return t('bootGuide.problems.diskLow', { role: t(`boot.servers.disks.roles.${String(p['role'])}`).toLowerCase(), free: formatBytes(Number(p['freeBytes'])) });
  }
}

function ProblemCard({
  card,
  access,
  venueSlug,
  scope,
  bootFull,
  now,
  onOpen,
}: {
  readonly card: BootProblemCard;
  readonly access: Access;
  readonly venueSlug: string;
  readonly scope: BootScope;
  readonly bootFull: ReadonlyMap<string, BootFloorPc>;
  readonly now: number;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const sentence = problemSentence(card, t, now);
  // "How long": for a PC problem, how long since its state began; for the boot server, the sentence already says.
  const since = card.kind === 'serverOffline' ? null : durationWords(card.sinceIso, now, t);
  const shown = card.numbers.slice(0, KEYCAPS_SHOWN);
  return (
    <>
      <span className="console-problem__icon" aria-hidden="true">
        <Icon name={card.tone === 'danger' ? 'alert' : 'clock'} />
      </span>
      <div className="console-problem__body">
        <p className="console-problem__text">{sentence}</p>
        {shown.length === 0 && since === null ? null : (
          <div className="console-problem__meta">
            {shown.length === 0 ? null : (
              <ul className="console-problem__pcs" aria-label={t('bootGuide.problems.pcs')}>
                {shown.map((number) => (
                  <li key={number} className="console-keycap">
                    {number}
                  </li>
                ))}
                {card.numbers.length > shown.length ? <li className="console-subtle">{t('bootGuide.problems.more', { count: card.numbers.length - shown.length })}</li> : null}
              </ul>
            )}
            {since === null ? null : <span className="console-subtle console-num">{t('bootGuide.problems.since', { duration: since })}</span>}
          </div>
        )}
      </div>
      <div className="console-problem__action">
        <ProblemAction card={card} access={access} venueSlug={venueSlug} scope={scope} bootFull={bootFull} onOpen={onOpen} />
      </div>
    </>
  );
}

function ProblemAction({
  card,
  access,
  venueSlug,
  scope,
  bootFull,
  onOpen,
}: {
  readonly card: BootProblemCard;
  readonly access: Access;
  readonly venueSlug: string;
  readonly scope: BootScope;
  readonly bootFull: ReadonlyMap<string, BootFloorPc>;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const can = (permission: string) => access.status === 'ready' && access.can(permission);
  const openServer = (
    <Link className="clav-button clav-button--secondary clav-button--sm" to="/v/$venueSlug/boot" params={{ venueSlug }}>
      {t('bootGuide.problems.openServer')}
    </Link>
  );
  switch (card.kind) {
    case 'noServer':
    case 'licence':
    case 'serverOffline':
    case 'notReaching':
    case 'diskLow':
      return openServer;
    case 'noImage':
      // Choosing an image for a class is the class's Windows & games, which needs both permissions.
      return can(STATIONS_MANAGE) && can(BOOT_MANAGE) ? (
        <Link className="clav-button clav-button--secondary clav-button--sm" to="/v/$venueSlug/stations" params={{ venueSlug }} search={{ tab: 'classes' } as never}>
          {typeof card.params['class'] === 'string' ? t('bootGuide.problems.chooseImage', { class: card.params['class'] }) : t('bootGuide.problems.chooseImageNoClass')}
        </Link>
      ) : null;
    case 'otherBoard':
      return <MotherboardHelp />;
    case 'startFailed': {
      const first = card.stationIds[0];
      return first === undefined ? null : (
        <Button variant="secondary" size="sm" onClick={() => onOpen(first)}>
          {t('bootGuide.problems.openPc')}
        </Button>
      );
    }
    case 'letter':
      return can(BOOT_MANAGE) ? (
        <Link className="clav-button clav-button--secondary clav-button--sm" to="/v/$venueSlug/boot" params={{ venueSlug }} search={{ page: 'games' } as never}>
          {t('bootGuide.problems.changeLetter')}
        </Link>
      ) : null;
    case 'behind':
      return can(BOOT_MANAGE) ? <RestartAll card={card} scope={scope} bootFull={bootFull} /> : null;
  }
}

/** The motherboard card's "What to do": a small centred explainer. */
function MotherboardHelp(): ReactNode {
  const { t } = useTranslation('console');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {t('bootGuide.problems.whatToDo')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen} size="sm" title={t('bootGuide.problems.otherBoardTitle')} description={t('bootGuide.problems.otherBoardIntro')}>
        <ul className="console-problem__steps">
          <li>{t('bootGuide.problems.otherBoardMake')}</li>
          <li>{t('bootGuide.problems.otherBoardMove')}</li>
        </ul>
        <div className="console-form__actions">
          <Button variant="primary" onClick={() => setOpen(false)}>
            {t('bootGuide.problems.otherBoardDone')}
          </Button>
        </div>
      </Dialog>
    </>
  );
}

/** Restart all when free: the rollout's own restart for exactly these PCs, one request per card (one image), confirmed first. */
function RestartAll({ card, scope, bootFull }: { readonly card: BootProblemCard; readonly scope: BootScope; readonly bootFull: ReadonlyMap<string, BootFloorPc> }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const restart = useRestartIdleBootPcs(scope);
  const [asking, setAsking] = useState(false);
  const imageId = String(card.params['imageId']);
  const ids = card.stationIds.flatMap((stationId) => {
    const pc = bootFull.get(stationId);
    return pc === undefined ? [] : [pc.bootPcId];
  });

  async function run(): Promise<void> {
    const result = await restart.mutateAsync({ imageId, bootPcIds: ids });
    show({ tone: result.restarted.length > 0 ? 'ok' : 'neutral', title: t('boot.floor.updates.result', { restarted: result.restarted.length, skipped: result.skipped.length }) });
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setAsking(true)}>
        {t('bootGuide.problems.restartAll')}
      </Button>
      {asking ? (
        <ConfirmDialog
          open
          onClose={() => setAsking(false)}
          title={t('boot.floor.updates.title')}
          description={t('boot.floor.updates.body')}
          confirmLabel={t('boot.floor.updates.confirm')}
          cancelLabel={t('common.cancel')}
          failedText={t('boot.floor.updates.failed')}
          onConfirm={run}
        />
      ) : null}
    </>
  );
}
